import React, { useState } from 'react';

import { Box, TextField, IconButton, Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Edit, Save, Close } from '@material-ui/icons';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import QualityEvaluationService from '../../services/quality-evaluation.service';

const useStyles = makeStyles(() => ({
  answerButton: {
    minWidth: 24,
  },
  answerIcon: {
    color: '#4caf50',
    fontSize: 24,
  },
  answers: {
    '&.confirmed': {
      backgroundColor: '#DEF9DA',
      borderColor: '#388E3C',
      color: '#388E3C',
    },
    '&.selected': {
      backgroundColor: '#DEF9DA',
      borderColor: '#388E3C',
      color: '#388E3C',
    },
    border: '1px solid #DEE8EF',
    borderRadius: 4,
    color: '#273B4A',
    fontSize: 12,
    padding: '7.5px 10px',
    textAlign: 'left',
  },
  answersGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  answersTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 8,
    textAlign: 'left',
    width: '100%',
  },
  buttonContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  editButton: {
    position: 'absolute',
    right: 16,
    top: 16,
  },
  editOptionField: {
    '& .MuiOutlinedInput-inputMarginDense': {
      fontSize: 14,
      padding: '8px 12px',
    },
    backgroundColor: '#FFF',
    marginBottom: 8,
  },
  editTitle: {
    fontSize: 16,
    marginLeft: 16,
    marginTop: 8,
    textAlign: 'left',
  },
  optionButton: {
    '&.correct': {
      backgroundColor: '#DEF9DA',
      borderColor: '#388E3C',
      color: '#388E3C',
    },
    border: '1px solid #DEE8EF',
    borderRadius: 4,
    color: '#273B4A',
    cursor: 'pointer',
    fontSize: 12,
    marginBottom: 8,
    padding: '7.5px 10px',
    textAlign: 'left',
    width: '100%',
  },
  optionsContainer: {
    backgroundColor: '#F9F9F9',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    margin: 0,
    padding: '12px 24px',
  },
  questionsContainer: {
    borderRadius: 5,
    boxShadow: '0px 0px 10px 0px #0000001A',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    justifyContent: 'start',
    marginBottom: 30,
    padding: '20px 20px 24px',
    position: 'relative',
  },
  questionsTitle: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'left',
    width: '100%',
  },
  shortQuizTitle: {
    fontSize: 18,
    fontWeight: 500,
    marginBottom: 10,
    textAlign: 'left',
  },
}));

const ObservationQuestions = ({ evaluation, observationId, isCompleted, fetchEvaluation }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editableQuestions, setEditableQuestions] = useState(evaluation?.questions ? [...evaluation.questions] : []);

  const canEdit = !isCompleted;

  const renderAnswers = (question) => {
    return question.options.map((option) => {
      const isCorrect = option.id === question.correctOptionId;
      const isSelected = evaluation?.answers && evaluation.answers[question.id] === option.id;

      let className = classes.answers;

      if (isCorrect) {
        className += ' confirmed';
      } else if (isSelected) {
        className += ' selected';
      }

      return (
        <Box key={option.id} className={className}>
          {option.text}
        </Box>
      );
    });
  };

  const handleEditQuestion = (index, value) => {
    const updatedQuestions = [...editableQuestions];
    updatedQuestions[index] = { ...updatedQuestions[index], text: value };

    setEditableQuestions(updatedQuestions);
  };

  const handleEditOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...editableQuestions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      text: value,
    };

    setEditableQuestions(updatedQuestions);
  };

  const handleSetCorrectOption = (questionIndex, optionId) => {
    const updatedQuestions = [...editableQuestions];
    updatedQuestions[questionIndex].correctOptionId = optionId;

    setEditableQuestions(updatedQuestions);
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);

      const updatePayload = {
        questions: editableQuestions.map((q) => ({
          correctOptionId: q.correctOptionId,
          id: q.id,
          options: q.options.map((o) => ({
            id: o.id,
            text: o.text,
          })),
          text: q.text,
        })),
      };

      await QualityEvaluationService.updateQuestions(observationId, updatePayload);

      enqueueSnackbar('Questions updated successfully', { variant: 'success' });

      setEditMode(false);

      fetchEvaluation();
    } catch (error) {
      console.error('Failed to update questions:', error);
      enqueueSnackbar('Failed to update questions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditableQuestions(evaluation?.questions ? [...evaluation.questions] : []);
    setEditMode(false);
  };

  if (!evaluation?.questions || evaluation.questions.length === 0) {
    return null;
  }

  return (
    <>
      <Box className={classes.shortQuizTitle}>Short Quiz</Box>

      <Box className={classes.questionsContainer}>
        {canEdit && !editMode && (
          <IconButton className={classes.editButton} size="small" onClick={() => setEditMode(true)}>
            <Edit />
          </IconButton>
        )}

        {!editMode ? (
          evaluation.questions.map((question) => (
            <Box key={question.id} className={classes.answersGroup} width={editMode ? '100%' : '460px'}>
              <Box className={classes.questionsTitle}>{question.text}</Box>
              {renderAnswers(question)}
            </Box>
          ))
        ) : (
          <>
            <Typography variant="subtitle" className={classes.editTitle}>
              You can modify your questions until the evaluation is completed.
            </Typography>

            {editableQuestions.map((question, questionIndex) => (
              <Box key={question.id} className={classes.answersGroup}>
                <Box className={classes.optionsContainer} mt={2} mb={1}>
                  <TextField
                    className={classes.editOptionField}
                    fullWidth
                    label={`Question ${questionIndex + 1}`}
                    value={question.text}
                    onChange={(e) => handleEditQuestion(questionIndex, e.target.value)}
                    variant="outlined"
                    size="small"
                    fontSize={12}
                    margin="normal"
                  />

                  {question.options.map((option, optionIndex) => (
                    <Box key={option.id}>
                      <TextField
                        className={classes.editOptionField}
                        label={`Answer ${optionIndex + 1}`}
                        fullWidth
                        value={option.text}
                        onChange={(e) => handleEditOption(questionIndex, optionIndex, e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <Button className={classes.answerButton} onClick={() => handleSetCorrectOption(questionIndex, option.id)} size="small">
                              {option.id === question.correctOptionId ? (
                                <CheckCircle className={classes.answerIcon} />
                              ) : (
                                <Cancel className={classes.answerIcon} style={{ color: '#AAA' }} />
                              )}
                            </Button>
                          ),
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            <Box className={classes.buttonContainer}>
              <Button onClick={handleCancelEdit} color="default" variant="outlined" startIcon={<Close />} style={{ marginRight: 8 }} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleSaveChanges} color="primary" variant="contained" startIcon={loading ? <CircularProgress size={20} /> : <Save />} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default ObservationQuestions;
